// Centralized route-aware SEO / head management.
// This module owns document.title, meta description, meta keywords,
// canonical, Open Graph and Twitter Card tags so that metadata
// never leaks between routes in the SPA.

export type SchemaType = 'website' | 'article' | 'faq' | 'webpage';

export interface SeoEntry {
  path: string;
  title: string;
  description: string;
  keywords: string;
  schemaType: SchemaType;
}

const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://cssbuybr.help').replace(/\/+$/, '');

const HOME: SeoEntry = {
  path: '/',
  title: 'CSSBuy Spreadsheet: Guides, Reviews, QC & Shipping',
  description:
    'Explore an independent CSSBuy Spreadsheet resource covering products, reviews, QC, shipping costs, warehouse guides and practical buying tips.',
  keywords:
    'CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy QC, CSSBuy guide, CSSBuy warehouse, CSSBuy coupon',
  schemaType: 'website',
};

const SEO_ROUTES: Record<string, SeoEntry> = {
  '/': HOME,
  '/en': HOME,
  '/pt-br': HOME,
  '/de': HOME,
  '/cssbuy-spreadsheet': {
    path: '/cssbuy-spreadsheet',
    title: 'CSSBuy Spreadsheet Complete Guide: Find, Compare & Research Products',
    description:
      'An independent guide explaining what CSSBuy Spreadsheet is, how to use it, and what to know before buying products through CSSBuy.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy product research, CSSBuy QC, CSSBuy shipping',
    schemaType: 'article',
  },
  '/guides': {
    path: '/guides',
    title: 'CSSBuy Guides: Spreadsheet, Shipping, QC & Safety',
    description:
      'Comprehensive CSSBuy guides covering spreadsheets, shipping, QC, warehouse and safety.',
    keywords: 'CSSBuy guides, CSSBuy spreadsheet, CSSBuy shipping, CSSBuy QC, CSSBuy warehouse',
    schemaType: 'article',
  },
  '/reviews': {
    path: '/reviews',
    title: 'CSSBuy Review 2026: Is CSSBuy Legit?',
    description:
      'Independent CSSBuy review covering shipping, QC, warehouse, costs and legitimacy.',
    keywords: 'CSSBuy review, CSSBuy legit, CSSBuy safe, CSSBuy review 2026',
    schemaType: 'article',
  },
  '/shipping': {
    path: '/shipping',
    title: 'CSSBuy Shipping Cost & Methods Guide',
    description:
      'Understand CSSBuy shipping costs, methods, weight calculations and delivery times.',
    keywords: 'CSSBuy shipping cost, CSSBuy shipping methods, CSSBuy shipping time',
    schemaType: 'article',
  },
  '/qc': {
    path: '/qc',
    title: 'CSSBuy QC Guide: How to Read QC Photos',
    description:
      'Learn how to read CSSBuy QC photos, check measurements and verify product details before shipping.',
    keywords: 'CSSBuy QC, CSSBuy quality check, CSSBuy QC photos',
    schemaType: 'article',
  },
  '/faq': {
    path: '/faq',
    title: 'CSSBuy Spreadsheet FAQ',
    description:
      'Frequently asked questions about CSSBuy Spreadsheet, QC, shipping and warehouse.',
    keywords: 'CSSBuy FAQ, CSSBuy spreadsheet FAQ, CSSBuy questions',
    schemaType: 'faq',
  },
  '/safety': {
    path: '/safety',
    title: 'CSSBuy Safety Center',
    description:
      'Essential safety tips for buying and researching through CSSBuy, protecting your account and money.',
    keywords: 'CSSBuy safety, CSSBuy security, CSSBuy scam protection',
    schemaType: 'article',
  },
  '/contact': {
    path: '/contact',
    title: 'Contact Us - CSSBuy Spreadsheet',
    description:
      'Contact the independent CSSBuy Spreadsheet resource hub with questions and feedback.',
    keywords: 'CSSBuy contact, CSSBuy support',
    schemaType: 'webpage',
  },
  '/privacy': {
    path: '/privacy',
    title: 'Privacy Policy - CSSBuy Spreadsheet',
    description: 'Privacy policy for cssbuybr.help, an independent CSSBuy Spreadsheet resource hub.',
    keywords: 'privacy policy, cssbuybr.help',
    schemaType: 'webpage',
  },
  '/terms': {
    path: '/terms',
    title: 'Terms of Service - CSSBuy Spreadsheet',
    description: 'Terms of service for cssbuybr.help, an independent CSSBuy Spreadsheet resource hub.',
    keywords: 'terms of service, cssbuybr.help',
    schemaType: 'webpage',
  },
  '/disclaimer': {
    path: '/disclaimer',
    title: 'Disclaimer - CSSBuy Spreadsheet',
    description: 'Disclaimer for cssbuybr.help, an independent CSSBuy Spreadsheet resource hub.',
    keywords: 'disclaimer, cssbuybr.help',
    schemaType: 'webpage',
  },
};

export function getSiteUrl(): string {
  return SITE_URL;
}

export function resolveSeo(pathname: string): SeoEntry | null {
  return SEO_ROUTES[pathname] ?? null;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applyHeadSeo(entry: SeoEntry | null): void {
  if (!entry) {
    return;
  }

  const canonical = `${SITE_URL}${entry.path === '/' ? '/' : entry.path}`;
  const ogType = entry.schemaType === 'article' ? 'article' : 'website';

  document.title = entry.title;

  upsertMeta('name', 'description', entry.description);
  upsertMeta('name', 'keywords', entry.keywords);
  upsertLink('canonical', canonical);

  // Open Graph
  upsertMeta('property', 'og:title', entry.title);
  upsertMeta('property', 'og:description', entry.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:type', ogType);

  // Twitter Card
  upsertMeta('name', 'twitter:card', 'summary');
  upsertMeta('name', 'twitter:title', entry.title);
  upsertMeta('name', 'twitter:description', entry.description);
}